import React from "react";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import ViewAccordion, { ViewAccordionField } from "../fields/ViewAccordion";

export interface ConnectionAccordionSectionProps<T> {
  title: string;
  loading: boolean;
  error?: string | null;
  items: T[];
  getItemId: (item: T, index: number) => string;
  getItemTitle: (item: T, index: number) => React.ReactNode;
  getItemSubtitle?: (item: T, index: number) => React.ReactNode;
  getItemFields: (item: T, index: number) => ViewAccordionField[];
  pageSize?: number;
  emptyText?: string;
  /**
   * Optional helper text rendered between the section title and the accordion list.
   */
  description?: React.ReactNode;
}

/**
 * Generic shell for view-mode connection-style sections.
 * Owns the section heading, loading / error / empty UI, and renders a `ViewAccordion`
 * with the per-item callbacks supplied by the caller.
 *
 * The caller is responsible for fetching its own data (each connection / associated
 * query has a slightly different signature, so data fetching is intentionally NOT
 * generalized here).
 */
const ConnectionAccordionSection = <T,>({
  title,
  loading,
  error,
  items,
  getItemId,
  getItemTitle,
  getItemSubtitle,
  getItemFields,
  pageSize = 10,
  emptyText = "No items",
  description,
}: ConnectionAccordionSectionProps<T>) => {
  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: "bold" }}>
        {title}
      </Typography>
      {description ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
      ) : null}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : items.length === 0 ? (
        <Typography variant="body1">{emptyText}</Typography>
      ) : (
        <ViewAccordion<T>
          items={items}
          getItemId={getItemId}
          getItemTitle={getItemTitle}
          getItemSubtitle={getItemSubtitle}
          getItemFields={getItemFields}
          allowMultiple={false}
          unmountOnExit
          headingLevel="h6"
          pageSize={pageSize}
        />
      )}
    </Box>
  );
};

export default ConnectionAccordionSection;
